/* ---------------------------------------------------------------------------
   Test results, and the register of hold points, NCRs and RFIs, on the site
   record (LIFECYCLE.md `LC-53`, `LC-54`).

   Two lists and their two forms, beside src/screens/works.ts's lists and
   src/screens/siteDialogs.ts's forms and drawn the same way: a list draws
   nothing where it holds nothing, and the page says so instead.

   THE PASS IS THE REGISTER'S. The form asks for the figures and never for a
   verdict, except a certificate's own statement; the list prints what the
   register derived, and a failure names the NCR it raised.

   EVERY ACT ON AN ITEM IS THE RESIDENT ENGINEER'S, so the control is drawn only
   for somebody holding `works.supervise` — and a release is refused by the
   endpoint to whoever raised the hold point, which the control says in its
   title rather than by hiding.
   --------------------------------------------------------------------------- */

import { h, type Child } from '../core/dom.ts';
import { actions, can, choiceKey, whyNot, type ItemDraft, type State, type TestDraft } from '../core/store.ts';
import { button, field, scrim, tag } from '../components/widgets.ts';
import { findBridge, prettyDate } from '../data/derive.ts';
import { activityLabel } from '../data/activities.ts';
import { componentChoices } from '../data/componentTree.ts';
import { normaliseIdentity } from '../data/roles.ts';
/* TEMPORARY — the FWO demonstration's count tag. Delete with src/data/fwo-demo.ts. */
import {
  INTEGRITY_CLASSES,
  INTEGRITY_METHODS,
  ITEM_ACT_LABEL,
  ITEM_KINDS,
  ITEM_STATUS_LABEL,
  POUR_ACTIVITIES,
  TEST_KINDS,
  nextAct,
  raiseCapability,
  testFamily,
  testKindLabel,
  testResultText,
  testSubject,
  type ItemKind,
  type ItemStatus,
} from '../data/siteQuality.ts';
import type { SiteItem } from '../data/types.ts';
import { dialogFoot, dialogHead } from './siteDialogs.ts';

/* =========================================================================
   THE LISTS
   ========================================================================= */

/** The results on this structure, newest first, failures marked and linked to their NCR. */
export function testList(s: Readonly<State>, headed = true): Child {
  if (s.progressRead !== 'read' || s.tests.length === 0) return null;
  const failed = s.tests.filter((t) => !t.passed).length;

  return h(
    'div',
    { class: [headed && 'works__sub'] },
    headed ? h('div', { class: 'works__sub-head', text: 'Test results' }) : null,
    h(
      'div',
      { class: 'works__totals' },
      h('span', { class: 'works__stat', text: `${s.tests.length - failed} passed` }),
      failed > 0
        ? h('span', { class: 'works__stat works__stat--draft', 'data-failed': String(failed), text: `${failed} failed` })
        : null,
    ),
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Test' }),
          h('th', { text: 'Of' }),
          h('th', { text: 'Result' }),
          h('th', { class: 'num', text: 'Tested' }),
          h('th', { text: '' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...s.tests.map((t) =>
          h(
            'tr',
            { 'data-test': t.id },
            h('td', { style: 'padding-left:14px', text: testKindLabel(t.kind) }),
            h('td', { text: [testSubject(t), testFamily(t.kind) === 'concrete' ? t.component : null].filter(Boolean).join(' · ') || '—' }),
            h('td', { text: testResultText(t) }),
            h('td', { class: 'num', text: prettyDate(t.testedOn) }),
            h(
              'td',
              {},
              t.passed ? tag('Pass', 1) : tag('Fail', 3),
              t.ncrRef ? h('span', { class: 'muted', text: ` ${t.ncrRef}` }) : null,
            ),
          ),
        ),
      ),
    ),
  );
}

/** What an item is about: the activity it holds, the part, or nothing named. */
function itemAbout(i: SiteItem): string {
  return i.activityName ?? i.component ?? '—';
}

/** The chip for an item's status: open is the loud one, closed the settled one. */
function statusTag(status: string): HTMLElement {
  const label = ITEM_STATUS_LABEL[status as ItemStatus] ?? status;
  return tag(label, status === 'closed' ? 1 : status === 'answered' ? 2 : 3);
}

/** The register on this structure: open first, a count per kind, the act under its row. */
export function itemList(s: Readonly<State>, headed = true): Child {
  if (s.progressRead !== 'read' || s.items.length === 0) return null;
  const maySupervise = can(s, 'works.supervise');
  const me = s.identity?.author ?? null;

  return h(
    'div',
    { class: [headed && 'works__sub'] },
    headed ? h('div', { class: 'works__sub-head', text: 'Hold points, NCRs and RFIs' }) : null,
    h(
      'div',
      { class: 'works__totals' },
      ...ITEM_KINDS.map((k) => {
        const open = s.items.filter((i) => i.kind === k.id && i.status !== 'closed').length;
        return h('span', {
          class: open > 0 ? 'works__stat works__stat--draft' : 'works__stat',
          'data-open': `${k.id}:${open}`,
          text: `${open} ${k.prefix} open`,
        });
      }),
    ),
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Ref' }),
          h('th', { text: 'Title' }),
          h('th', { text: 'About' }),
          h('th', { class: 'num', text: 'Due' }),
          h('th', { text: 'Status' }),
          maySupervise ? h('th', { text: '' }) : null,
        ),
      ),
      h(
        'tbody',
        {},
        ...s.items.flatMap((i) => {
          const step = nextAct(i);
          const acting = s.itemAct?.id === i.id ? s.itemAct : null;
          const own =
            step?.act === 'release' && me !== null && i.raisedBy !== null &&
            normaliseIdentity(me) === normaliseIdentity(i.raisedBy);
          const row = h(
            'tr',
            { class: [i.status === 'closed' && 'is-muted'], 'data-item': i.ref },
            h('td', { style: 'padding-left:14px', class: 'display', text: i.ref }),
            h(
              'td',
              {},
              h('div', { text: i.title }),
              i.answer ? h('div', { class: 'muted', text: `Answer: ${i.answer}` }) : null,
              i.closeNote
                ? h('div', {
                    class: 'muted',
                    text: `${i.kind === 'hold-point' ? 'Released' : 'Closed'}${i.closedBy ? ` by ${i.closedBy}` : ''}${
                      i.closedAt ? ` ${prettyDate(i.closedAt.slice(0, 10))}` : ''
                    }: ${i.closeNote}`,
                  })
                : null,
            ),
            h('td', { text: itemAbout(i) }),
            h('td', { class: 'num', text: i.dueOn ? prettyDate(i.dueOn) : '—' }),
            h('td', {}, statusTag(i.status)),
            maySupervise
              ? h(
                  'td',
                  {},
                  step && !acting
                    ? button(ITEM_ACT_LABEL[step.act], {
                        sm: true,
                        disabled: own,
                        title: own
                          ? 'You raised this hold point, so somebody else must release it'
                          : `${ITEM_ACT_LABEL[step.act]} ${i.ref}`,
                        onClick: () => actions.openItemAct(i.id, step.act),
                      })
                    : null,
                )
              : null,
          );
          return acting ? [row, actRow(s, acting)] : [row];
        }),
      ),
    ),
  );
}

/** The answer, close-out or release of one item, under its row. */
function actRow(s: Readonly<State>, a: NonNullable<State['itemAct']>): HTMLElement {
  return h(
    'tr',
    { class: 'works__close' },
    h(
      'td',
      { colspan: 6, style: 'padding-left:14px' },
      h(
        'div',
        { class: 'works__close-form' },
        h('textarea', {
          id: 'item-act-note',
          class: 'input',
          rows: 2,
          'aria-label': a.act === 'answer' ? 'Answer' : 'Close-out note',
          placeholder: a.act === 'answer' ? 'The answer' : 'What was found',
          value: a.note,
          oninput: (e: Event) => actions.setItemActNote((e.target as HTMLTextAreaElement).value),
        }),
        button(ITEM_ACT_LABEL[a.act], { sm: true, primary: true, onClick: () => void actions.submitItemAct() }),
        button('Cancel', { sm: true, onClick: () => actions.cancelItemAct() }),
        s.itemActError ? h('span', { class: 'dlg__note dlg__note--error', text: s.itemActError }) : null,
      ),
    ),
  );
}

/* =========================================================================
   THE FORMS
   ========================================================================= */

/**
 * The parts a result or an item can be about: the model's, one per part where
 * it has ids, or — with no model on screen — the parts the site has recorded
 * work against.
 */
function partChoices(s: Readonly<State>): { key: string; text: string }[] {
  const tree = s.heroComponentTree ?? [];
  if (tree.length) {
    return componentChoices(tree).map((c) => ({ key: choiceKey(c.name, c.id), text: c.text }));
  }
  const seen = new Set<string>();
  const out: { key: string; text: string }[] = [];
  for (const e of s.progress) {
    const key = choiceKey(e.component, e.componentId ?? null);
    if (key === '' || seen.has(key)) continue;
    seen.add(key);
    out.push({ key, text: e.component! });
  }
  return out;
}

function partSelect(id: string, value: string, s: Readonly<State>, onchange: (e: Event) => void): HTMLElement {
  return h(
    'select',
    { id, class: 'select', onchange },
    h('option', { value: '', selected: value === '', text: 'Not recorded' }),
    ...partChoices(s).map((c) => h('option', { value: c.key, selected: c.key === value, text: c.text })),
  );
}

function numberInput(id: string, value: string, set: (e: Event) => void, step = 'any'): HTMLElement {
  return h('input', { id, class: 'input', type: 'number', inputmode: 'decimal', step, min: '0', value, oninput: set });
}

export const TEST_TITLE = 'Record a test result';

/** The test-result form, or null when it is closed. A modal, as the document form is. */
export function testDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.testBridgeId) return null;
  const b = findBridge(s.testBridgeId);
  if (b === null) return null;

  const d = s.testDraft;
  const family = testFamily(d.kind);
  const set = (key: keyof TestDraft) => (e: Event) =>
    actions.setTestField(key, (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value);
  const pours = s.progress.filter((e) => !e.pending && POUR_ACTIVITIES.has(e.activity)).slice(0, 60);
  const wide = (child: HTMLElement) => h('div', { style: 'grid-column: 1 / -1' }, child);

  const figures: HTMLElement[] =
    family === 'concrete'
      ? [
          field('Mix or grade', h('input', { id: 'test-mix', class: 'input', placeholder: 'C30/37', value: d.mix, oninput: set('mix') }), 'test-mix'),
          field('Age, days', numberInput('test-age', d.ageDays, set('ageDays'), '1'), 'test-age'),
          field('Strength, MPa', numberInput('test-strength', d.strengthMpa, set('strengthMpa')), 'test-strength'),
          field('Required at this age, MPa', numberInput('test-required', d.requiredMpa, set('requiredMpa')), 'test-required'),
          wide(
            field(
              'From the pour',
              h(
                'select',
                { id: 'test-pour', class: 'select', onchange: set('eventId') },
                h('option', { value: '', selected: d.eventId === '', text: 'Not recorded' }),
                ...pours.map((e) =>
                  h('option', {
                    value: e.id,
                    selected: e.id === d.eventId,
                    text: `${e.occurredOn} · ${activityLabel(e.activity)}${e.component ? ` · ${e.component}` : ''}`,
                  }),
                ),
              ),
              'test-pour',
            ),
          ),
        ]
      : family === 'integrity'
        ? [
            wide(field('Pile', partSelect('test-part', d.component, s, set('component')), 'test-part')),
            field(
              'Method',
              h(
                'select',
                { id: 'test-method', class: 'select', onchange: set('method') },
                h('option', { value: '', selected: d.method === '', text: 'Choose…' }),
                ...INTEGRITY_METHODS.map((m) => h('option', { value: m.id, selected: m.id === d.method, text: m.label })),
              ),
              'test-method',
            ),
            field(
              'Result class',
              h(
                'select',
                { id: 'test-class', class: 'select', onchange: set('resultClass') },
                h('option', { value: '', selected: d.resultClass === '', text: 'Choose…' }),
                ...INTEGRITY_CLASSES.map((c) => h('option', { value: c.id, selected: c.id === d.resultClass, text: c.label })),
              ),
              'test-class',
            ),
          ]
        : family === 'load'
          ? [
              wide(field('Pile', partSelect('test-part', d.component, s, set('component')), 'test-part')),
              field('Test load, kN', numberInput('test-load', d.testLoadKn, set('testLoadKn')), 'test-load'),
              field('Settlement, mm', numberInput('test-settle', d.settlementMm, set('settlementMm')), 'test-settle'),
              field('Settlement allowed, mm', numberInput('test-allowed', d.allowedMm, set('allowedMm')), 'test-allowed'),
            ]
          : family === 'certificate'
            ? [
                field('Heat or lot', h('input', { id: 'test-lot', class: 'input', value: d.lot, oninput: set('lot') }), 'test-lot'),
                field('Grade', h('input', { id: 'test-grade', class: 'input', placeholder: d.kind === 'strand-cert' ? 'Grade 270' : 'Grade 60', value: d.grade, oninput: set('grade') }), 'test-grade'),
                field(
                  'The certificate says it',
                  h(
                    'select',
                    { id: 'test-complies', class: 'select', onchange: set('complies') },
                    h('option', { value: '', selected: d.complies === '', text: 'Choose…' }),
                    h('option', { value: 'yes', selected: d.complies === 'yes', text: 'Complies' }),
                    h('option', { value: 'no', selected: d.complies === 'no', text: 'Does not comply' }),
                  ),
                  'test-complies',
                ),
              ]
            : [];

  return h(
    'div',
    { class: 'dlg', ...scrim(() => actions.closeTest(), () => s.testSaving) },
    h(
      'form',
      {
        class: 'dlg__panel dlg__panel--sm',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': `${TEST_TITLE} — ${b.name}`,
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveTest();
        },
      },
      dialogHead(`${b.id} · test`, TEST_TITLE, () => actions.closeTest()),
      s.testError ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.testError })) : null,
      h(
        'div',
        { class: 'dlg__body' },
        h(
          'div',
          { class: 'dlg__form-grid' },
          field(
            'Kind',
            h(
              'select',
              { id: 'test-kind', class: 'select', onchange: set('kind') },
              h('option', { value: '', selected: d.kind === '', text: 'Choose…' }),
              ...TEST_KINDS.map((k) => h('option', { value: k.id, selected: k.id === d.kind, text: k.label })),
            ),
            'test-kind',
          ),
          field(
            'Tested on',
            h('input', { id: 'test-date', class: 'input', type: 'date', required: true, value: d.testedOn, oninput: set('testedOn') }),
            'test-date',
          ),
          ...figures,
          family
            ? wide(
                field(
                  'Certificate on file',
                  h(
                    'select',
                    { id: 'test-doc', class: 'select', onchange: set('documentId') },
                    h('option', { value: '', selected: d.documentId === '', text: 'None' }),
                    ...s.documents
                      .filter((doc) => !doc.fileUrl)
                      .map((doc) => h('option', { value: doc.id, selected: doc.id === d.documentId, text: doc.title })),
                  ),
                  'test-doc',
                ),
              )
            : null,
          family
            ? wide(field('Note', h('textarea', { id: 'test-note', class: 'input', rows: 2, value: d.note, oninput: set('note') }), 'test-note'))
            : null,
        ),
      ),
      dialogFoot(() => actions.closeTest(), s.testSaving ? 'Recording…' : 'Record', s.testSaving),
    ),
  );
}

export const ITEM_TITLE = 'Raise a hold point, NCR or RFI';

/** The form raising an item, or null when it is closed. */
export function itemDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.itemBridgeId) return null;
  const b = findBridge(s.itemBridgeId);
  if (b === null) return null;

  const d = s.itemDraft;
  const set = (key: keyof ItemDraft) => (e: Event) =>
    actions.setItemField(key, (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value);
  const activities = (s.baseline?.activities ?? [])
    .filter((a) => !a.isGroup)
    .sort((x, y) => x.plannedStart.localeCompare(y.plannedStart));
  const wide = (child: HTMLElement) => h('div', { style: 'grid-column: 1 / -1' }, child);

  return h(
    'div',
    { class: 'dlg', ...scrim(() => actions.closeItem(), () => s.itemSaving) },
    h(
      'form',
      {
        class: 'dlg__panel dlg__panel--sm',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': `${ITEM_TITLE} — ${b.name}`,
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveItem();
        },
      },
      dialogHead(`${b.id} · register`, ITEM_TITLE, () => actions.closeItem()),
      s.itemError ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.itemError })) : null,
      h(
        'div',
        { class: 'dlg__body' },
        h(
          'div',
          { class: 'dlg__block' },
          field(
            'Kind',
            h(
              'div',
              { class: 'actpick', role: 'radiogroup', 'aria-label': 'Kind of item' },
              ...ITEM_KINDS.map((k) => {
                const may = can(s, raiseCapability(k.id));
                return h(
                  'button',
                  {
                    type: 'button',
                    class: ['actpick__row', k.id === d.kind && 'actpick__row--on'],
                    role: 'radio',
                    'aria-checked': k.id === d.kind ? 'true' : 'false',
                    disabled: !may,
                    title: may ? k.label : whyNot(s, `Raising ${k.label.toLowerCase()}`, raiseCapability(k.id)),
                    onclick: () => actions.setItemField('kind', k.id),
                  },
                  h('span', { class: 'actpick__label', text: k.label }),
                );
              }),
            ),
          ),
        ),
        h(
          'div',
          { class: 'dlg__form-grid' },
          wide(field('Title', h('input', { id: 'item-title', class: 'input', required: true, value: d.title, oninput: set('title') }), 'item-title')),
          wide(
            field(
              d.kind === 'hold-point' ? 'The activity it holds' : 'Planned activity',
              h(
                'select',
                { id: 'item-activity', class: 'select', onchange: set('activityId') },
                h('option', { value: '', selected: d.activityId === '', text: activities.length ? 'None' : 'No programme frozen' }),
                ...activities.map((a) =>
                  h('option', { value: a.activityId, selected: a.activityId === d.activityId, text: `${a.plannedStart} · ${a.name}` }),
                ),
              ),
              'item-activity',
            ),
          ),
          wide(field('Part', partSelect('item-part', d.component, s, set('component')), 'item-part')),
          field('Owner', h('input', { id: 'item-owner', class: 'input', value: d.owner, oninput: set('owner') }), 'item-owner'),
          field('Due', h('input', { id: 'item-due', class: 'input', type: 'date', value: d.dueOn, oninput: set('dueOn') }), 'item-due'),
          wide(field('Detail', h('textarea', { id: 'item-detail', class: 'input', rows: 3, value: d.detail, oninput: set('detail') }), 'item-detail')),
        ),
      ),
      dialogFoot(() => actions.closeItem(), s.itemSaving ? 'Raising…' : 'Raise', s.itemSaving),
    ),
  );
}

/**
 * Whether a change to either draft needs the dialog layer redrawn: the kind,
 * which changes which fields there are. Everything else is drawn by its input.
 */
export function qualityDialogsNeedPaint(
  a: { testDraft: TestDraft; itemDraft: ItemDraft },
  b: { testDraft: TestDraft; itemDraft: ItemDraft },
): boolean {
  return a.testDraft.kind !== b.testDraft.kind || a.itemDraft.kind !== b.itemDraft.kind;
}

/** The kinds a person may raise, for the buttons that open the form. */
export function mayRaiseAny(s: Readonly<State>): boolean {
  return ITEM_KINDS.some((k) => can(s, raiseCapability(k.id as ItemKind)));
}
